import { useCallback, useEffect, useState } from 'react'
import { Search, Plus, Pencil, Trash2, RefreshCw, Workflow, Eye } from 'lucide-react'
import Button from '../components/forms/Button'
import Input from '../components/forms/Input'
import Modal from '../components/Modal'
import RuleSetDrawer from '../components/rules-engine/RuleSetDrawer'
import RuleSetViewDrawer from '../components/rules-engine/RuleSetViewDrawer'
import { useToast } from '../contexts/ToastContext'
import { useTheme } from '../contexts/ThemeContext'
import { deleteRuleSet, getRuleSetRulesCount, getRuleSet, listRuleSets } from '../services/rulesEngineService'
import type { RuleSetRecord } from '../types/rulesEngine'

export default function RulesEngine() {
  const { theme } = useTheme()
  const { showToast } = useToast()

  const [ruleSets, setRuleSets] = useState<RuleSetRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create')
  const [drawerRuleSet, setDrawerRuleSet] = useState<RuleSetRecord | null>(null)
  const [isOpeningEdit, setIsOpeningEdit] = useState(false)

  const [viewOpen, setViewOpen] = useState(false)
  const [viewRuleSetId, setViewRuleSetId] = useState<string | null>(null)
  const [viewFallback, setViewFallback] = useState<RuleSetRecord | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<RuleSetRecord | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300)
    return () => window.clearTimeout(handle)
  }, [searchTerm])

  const loadRuleSets = useCallback(async (q?: string) => {
    setIsLoading(true)
    try {
      const rows = await listRuleSets(q)
      setRuleSets(rows)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Unable to load rule sets.', {
        type: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    void loadRuleSets(debouncedSearch || undefined)
  }, [debouncedSearch, loadRuleSets])

  const openCreateDrawer = () => {
    setDrawerMode('create')
    setDrawerRuleSet(null)
    setDrawerOpen(true)
  }

  const openViewDrawer = (set: RuleSetRecord) => {
    setViewRuleSetId(set.id)
    setViewFallback(set)
    setViewOpen(true)
  }

  const openEditDrawer = async (set: RuleSetRecord) => {
    setIsOpeningEdit(true)
    try {
      const fresh = (await getRuleSet(set.id)) ?? set
      setDrawerMode('edit')
      setDrawerRuleSet(fresh)
      setDrawerOpen(true)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Unable to load rule set.', {
        type: 'error',
      })
    } finally {
      setIsOpeningEdit(false)
    }
  }

  const handleDrawerSaved = (record: RuleSetRecord) => {
    setRuleSets((prev) => {
      const exists = prev.some((r) => r.id === record.id)
      const next = exists
        ? prev.map((r) => (r.id === record.id ? record : r))
        : [...prev, record]
      return next.sort((a, b) => a.name.localeCompare(b.name))
    })
    setDrawerRuleSet((prev) => (prev && prev.id === record.id ? record : prev))
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteRuleSet(deleteTarget.id)
      setRuleSets((prev) => prev.filter((r) => r.id !== deleteTarget.id))
      setDeleteTarget(null)
      showToast('Rule set deleted.', { type: 'success' })
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Unable to delete rule set.', {
        type: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b px-4 py-5 sm:px-6" style={{ borderColor: theme.colors.border }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className="grid h-10 w-10 place-items-center rounded-xl"
              style={{ backgroundColor: theme.colors.buttonPrimary, color: '#fff' }}
            >
              <Workflow className="h-5 w-5" />
            </div>
            <div>
              <p
                className="text-[11px] font-semibold uppercase tracking-widest"
                style={{ color: theme.colors.textMuted }}
              >
                Patient Intake
              </p>
              <h1 className="text-xl font-bold sm:text-2xl" style={{ color: theme.colors.textPrimary }}>
                Benefits Rules Engine
              </h1>
              <p className="mt-1 text-sm" style={{ color: theme.colors.textSecondary }}>
                Define and manage benefit rule sets. Run rules from a patient&apos;s detail page.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadRuleSets(debouncedSearch || undefined)}
              disabled={isLoading}
            >
              <span className="inline-flex items-center gap-2">
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </span>
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={openCreateDrawer}>
              <span className="inline-flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Rule Set
              </span>
            </Button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
              style={{ color: theme.colors.textMuted }}
            />
            <Input
              name="rule-set-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search rule sets..."
              className="pl-9"
            />
          </div>

          <div
            className="overflow-hidden rounded-xl border"
            style={{ borderColor: theme.colors.cardBorder, backgroundColor: theme.colors.cardBackground }}
          >
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead style={{ backgroundColor: theme.colors.background }}>
                  <tr>
                    {['Name', 'Product Service', 'Rules', 'Status', 'Actions'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide"
                        style={{ color: theme.colors.textMuted }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ruleSets.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center" style={{ color: theme.colors.textMuted }}>
                        {isLoading ? 'Loading rule sets...' : 'No rule sets found.'}
                      </td>
                    </tr>
                  ) : (
                    ruleSets.map((set) => (
                      <tr key={set.id} style={{ borderTop: `1px solid ${theme.colors.border}` }}>
                        <td className="px-4 py-3 font-medium" style={{ color: theme.colors.textPrimary }}>
                          {set.name}
                        </td>
                        <td className="px-4 py-3" style={{ color: theme.colors.textSecondary }}>
                          {set.productServiceSlug || '—'}
                        </td>
                        <td className="px-4 py-3" style={{ color: theme.colors.textSecondary }}>
                          {getRuleSetRulesCount(set)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="rounded px-2 py-0.5 text-[11px] font-semibold"
                            style={{
                              color: set.isActive ? '#0d9488' : theme.colors.textMuted,
                              backgroundColor: set.isActive ? '#f0fdfa' : theme.colors.background,
                            }}
                          >
                            {set.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openViewDrawer(set)}
                              aria-label={`View ${set.name}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => void openEditDrawer(set)}
                              disabled={isOpeningEdit}
                              aria-label={`Edit ${set.name}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => setDeleteTarget(set)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 p-4 md:hidden">
              {ruleSets.map((set) => (
                <div key={set.id} className="rounded-xl border p-4" style={{ borderColor: theme.colors.border }}>
                  <div className="font-semibold" style={{ color: theme.colors.textPrimary }}>
                    {set.name}
                  </div>
                  <p className="mt-1 text-xs" style={{ color: theme.colors.textSecondary }}>
                    {getRuleSetRulesCount(set)} rules · {set.productServiceSlug || 'No product slug'}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => openViewDrawer(set)}>
                      View
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void openEditDrawer(set)}
                      disabled={isOpeningEdit}
                    >
                      Edit
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setDeleteTarget(set)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <RuleSetDrawer
        open={drawerOpen}
        mode={drawerMode}
        ruleSet={drawerRuleSet}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleDrawerSaved}
      />

      <RuleSetViewDrawer
        open={viewOpen}
        ruleSetId={viewRuleSetId}
        fallback={viewFallback}
        onClose={() => {
          setViewOpen(false)
          setViewRuleSetId(null)
        }}
      />

      <Modal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete Rule Set" size="sm">
        <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
          Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={() => void handleDelete()} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  )
}
